import React, { useState, useEffect } from 'react';
import { Search, Heart, Briefcase, MapPin, Filter, X, CheckCircle, DollarSign, Building2, Clock, Calendar, Mail, Phone, Globe, ExternalLink, ChevronRight, Bot, Info } from 'lucide-react';
import {
  getAllADAJobs,
  trackJobSeekerInterest
} from '../utils/adaJobRequirements';

const JobSeekerTracker = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seekerId, setSeekererId] = useState(localStorage.getItem('jobSeekerId') || null);
  const [showIdModal, setShowIdModal] = useState(!seekerId);
  const [tempId, setTempId] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [trackedJobs, setTrackedJobs] = useState(new Set());
  
  // UI states
  const [selectedJob, setSelectedJob] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [crawlInfo, setCrawlInfo] = useState(null);
  const [showCrawlBanner, setShowCrawlBanner] = useState(true);

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, industryFilter, levelFilter, locationFilter, trackedJobs]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const allJobs = await getAllADAJobs();
      const activeJobs = Array.isArray(allJobs) ? allJobs.filter(j => (j.status || 'active') === 'active') : [];
      setJobs(activeJobs);
      
      // Also fetch data status for info banner (CLIENT-SIDE)
      try {
        const { getCrawlStatus } = await import('../utils/adaJobRequirements');
        const status = await getCrawlStatus();
        if (status) setCrawlInfo(status);
      } catch (e) { /* non-critical */ }
    } catch (error) {
      console.error('Error loading jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(j =>
        (j.jobTitle || j.title || '').toLowerCase().includes(term) ||
        (j.company || '').toLowerCase().includes(term) ||
        (j.industry || '').toLowerCase().includes(term) ||
        (j.location || '').toLowerCase().includes(term) ||
        (j.description || '').toLowerCase().includes(term)
      );
    }
    if (industryFilter) filtered = filtered.filter(j => j.industry === industryFilter);
    if (levelFilter) filtered = filtered.filter(j => (j.level || j.experience || '') === levelFilter);
    if (locationFilter) filtered = filtered.filter(j => (j.location || '').includes(locationFilter));

    setFilteredJobs(filtered);
  };

  const handleSetSeekerID = () => {
    if (tempId.trim()) {
      localStorage.setItem('jobSeekerId', tempId);
      setSeekererId(tempId);
      setShowIdModal(false);
      setTempId('');
    }
  };

  const handleTrackJob = (jobId, status) => {
    if (!seekerId) { setShowIdModal(true); return; }
    try {
      trackJobSeekerInterest(seekerId, jobId, status);
      setTrackedJobs(prev => new Set(prev).add(`${jobId}-${status}`));
      setSuccessMessage(`Job marked as "${status}"!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error tracking job:', error);
    }
  };

  const industries = [...new Set(jobs.map(j => j.industry).filter(Boolean))];
  const levels = [...new Set(jobs.map(j => j.level || j.experience).filter(Boolean))];
  const locations = [...new Set(jobs.map(j => {
    const loc = j.location || '';
    const parts = loc.split(',');
    return parts.length > 1 ? parts[parts.length - 1].trim() : loc;
  }).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8">
      {/* ID Modal */}
      {showIdModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
            <h2 className="text-2xl font-bold mb-4">Welcome to ADA Job Opportunities</h2>
            <p className="text-gray-600 mb-6">Enter your email or ID to track your job interests.</p>
            <input
              type="text"
              value={tempId}
              onChange={(e) => setTempId(e.target.value)}
              placeholder="e.g., john@example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
              onKeyDown={(e) => e.key === 'Enter' && handleSetSeekerID()}
            />
            <button onClick={handleSetSeekerID} className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
              Continue
            </button>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ADA-Compliant Job Opportunities</h1>
          <p className="text-gray-600">
            Find jobs with ADA compliance focus — full details including company, salary, location & contacts
            {seekerId && <span className="text-blue-600 ml-2">• {seekerId}</span>}
          </p>
          {seekerId && (
            <button onClick={() => { localStorage.removeItem('jobSeekerId'); setSeekererId(null); }}
              className="text-sm text-gray-500 hover:text-gray-700 mt-1 underline">Switch Account</button>
          )}
        </div>

        {/* Crawling Info Caveat Banner */}
        {showCrawlBanner && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 relative">
            <button onClick={() => setShowCrawlBanner(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 rounded-lg p-2 mt-0.5">
                <Bot className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 text-sm mb-1">🤖 AI-Powered Job Crawling</h3>
                <p className="text-xs text-blue-800 mb-2">
                  These job opportunities are harvested directly from public job boards (Indeed, LinkedIn, USAJobs) using Nibyor's crawler. Listings reflect real postings from the web filtered by date, country and accessibility keywords.  "Search Jobs" buttons link to the actual listings; contacts are scraped from the posting and should be verified before use.
                </p>

              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-lg flex items-center gap-2 border border-green-200">
            <CheckCircle className="w-5 h-5" /> {successMessage}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input type="text" placeholder="Search jobs, companies, locations..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <select value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All Industries</option>
              {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
            <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All Levels</option>
              <option value="entry">Entry Level</option>
              <option value="mid">Mid Level</option>
              <option value="senior">Senior Level</option>
              <option value="lead">Lead</option>
              <option value="director">Director</option>
            </select>
            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">All Locations</option>
              {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          </div>
          {(searchTerm || industryFilter || levelFilter || locationFilter) && (
            <button onClick={() => { setSearchTerm(''); setIndustryFilter(''); setLevelFilter(''); setLocationFilter(''); }}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              <X className="w-4 h-4" /> Clear all filters
            </button>
          )}
        </div>

        {/* Results Count */}
        <p className="text-sm text-gray-500 mb-4">{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} found</p>

        {/* Jobs List */}
        <div className="space-y-4 mb-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Loading jobs...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl shadow">
              <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No jobs match your filters</p>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your search criteria</p>
            </div>
          ) : (
            filteredJobs.map(job => (
              <JobCardRich
                key={job.id}
                job={job}
                onSelect={() => setSelectedJob(job)}
                onTrack={(status) => handleTrackJob(job.id, status)}
                isTracked={trackedJobs.has(`${job.id}-interested`)}
              />
            ))
          )}
        </div>

        {/* Job Detail Modal */}
        {selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-start z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedJob.jobTitle || selectedJob.title}</h2>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> {selectedJob.company}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {selectedJob.location}</span>
                    <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> {selectedJob.salary}</span>
                  </div>
                </div>
                <button onClick={() => setSelectedJob(null)} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">{(selectedJob.level || selectedJob.experience || '').charAt(0).toUpperCase() + (selectedJob.level || selectedJob.experience || '').slice(1)} Level</span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">{selectedJob.industry}</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">{selectedJob.employmentType || selectedJob.type || 'Full-Time'}</span>
                  {selectedJob.remotePolicy && <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">{selectedJob.remotePolicy}</span>}
                </div>

                {/* Company Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Company Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <p><span className="text-gray-500">Company:</span> <span className="font-medium">{selectedJob.company}</span></p>
                    {selectedJob.companySize && <p><span className="text-gray-500">Size:</span> {selectedJob.companySize} employees</p>}
                    {selectedJob.department && <p><span className="text-gray-500">Department:</span> {selectedJob.department}</p>}
                    {(selectedJob.companyWebsite || selectedJob.company_website) && (
                      <p className="flex items-center gap-1"><Globe className="w-3.5 h-3.5 text-gray-400" />
                        <a href={selectedJob.companyWebsite || selectedJob.company_website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedJob.companyWebsite || selectedJob.company_website}</a>
                      </p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">About This Role</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedJob.description}</p>
                </div>

                {/* Responsibilities */}
                {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Key Responsibilities</h3>
                    <ul className="space-y-1.5">
                      {selectedJob.responsibilities.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <ChevronRight className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Qualifications */}
                {selectedJob.qualifications && selectedJob.qualifications.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Qualifications</h3>
                    <ul className="space-y-1.5">
                      {selectedJob.qualifications.map((q, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preferred Skills */}
                {(selectedJob.preferredSkills || selectedJob.skills || []).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Preferred Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {(selectedJob.preferredSkills || selectedJob.skills || []).map((s, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Benefits */}
                {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Benefits & Perks</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {selectedJob.benefits.map((b, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-green-800">
                          <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Contact & Application */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">How to Apply</h3>
                  <div className="space-y-1.5 text-sm">
                    {selectedJob.contactPerson && <p><span className="text-gray-500">Contact:</span> <span className="font-medium">{selectedJob.contactPerson}</span></p>}
                    {selectedJob.contactEmail && (
                      <p className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-blue-500" />
                        <a href={`mailto:${selectedJob.contactEmail}`} className="text-blue-600 hover:underline">{selectedJob.contactEmail}</a></p>
                    )}
                    {selectedJob.contactPhone && (
                      <p className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-blue-500" /> {selectedJob.contactPhone}</p>
                    )}
                    {selectedJob.applicationDeadline && (
                      <p className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-orange-700 font-medium">Apply by: {selectedJob.applicationDeadline}</span></p>
                    )}
                    {(selectedJob.applicationUrl || selectedJob.apply_url) && (
                      <a href={selectedJob.applicationUrl || selectedJob.apply_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                        <ExternalLink className="w-4 h-4" /> Search on Indeed / Job Boards
                      </a>
                    )}
                    <p className="mt-2 text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-md">
                      ⚠️ Contact details are AI-generated leads — verify independently before reaching out. "Search on Indeed" links to real job board search results.
                    </p>
                  </div>
                </div>

                {/* Dates */}
                <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Posted: {formatDate(selectedJob.postedDate || selectedJob.posted_date)}</span>
                  {selectedJob.crawledAt && <span>Crawled: {formatDate(selectedJob.crawledAt)}</span>}
                  <span>Source: {selectedJob.source || 'AI Generated'}</span>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
                <button onClick={() => { handleTrackJob(selectedJob.id, 'interested'); setSelectedJob(null); }}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium">
                  <Heart className="w-4 h-4" /> Interested
                </button>
                <button onClick={() => { handleTrackJob(selectedJob.id, 'applied'); setSelectedJob(null); }}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
                  Applied
                </button>
                <button onClick={() => setSelectedJob(null)}
                  className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Rich Job Card =====
const JobCardRich = ({ job, onSelect, onTrack, isTracked }) => {
  const levelColors = {
    entry: 'bg-green-100 text-green-800',
    mid: 'bg-blue-100 text-blue-800',
    'mid-level': 'bg-blue-100 text-blue-800',
    senior: 'bg-purple-100 text-purple-800',
    lead: 'bg-orange-100 text-orange-800',
    director: 'bg-red-100 text-red-800'
  };

  const jobTitle = job.jobTitle || job.title || '';
  const jobLevel = job.level || job.experience || '';
  const jobEmploymentType = job.employmentType || job.type || 'Full-Time';
  const jobPostedDate = job.postedDate || job.posted_date || '';
  const jobApplicationUrl = job.applicationUrl || job.apply_url || '';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition overflow-hidden">
      <div className="p-5">
        {/* Top Row */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 hover:text-blue-600 cursor-pointer" onClick={onSelect}>{jobTitle}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-600">
              <span className="flex items-center gap-1 font-medium"><Building2 className="w-3.5 h-3.5 text-gray-400" /> {job.company}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {job.location || 'Remote'}</span>
              <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5 text-gray-400" /> {job.salary || 'Competitive'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onTrack('interested')}
              className={`p-2 rounded-lg transition ${isTracked ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
              <Heart className={`w-5 h-5 ${isTracked ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${levelColors[jobLevel.toLowerCase()] || 'bg-gray-100 text-gray-700'}`}>
            {jobLevel.charAt(0).toUpperCase() + jobLevel.slice(1)}
          </span>
          <span className="px-2.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{job.industry}</span>
          <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">{jobEmploymentType}</span>
          {job.remotePolicy && <span className="px-2.5 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">{job.remotePolicy}</span>}
          {job.department && <span className="px-2.5 py-0.5 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium">{job.department}</span>}
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{job.description}</p>

        {/* Key details */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
          {job.companySize && <span>🏢 {job.companySize} employees</span>}
          <span>📅 Posted: {formatDate(jobPostedDate)}</span>
          {job.applicationDeadline && <span className="text-orange-600 font-medium">⏰ Deadline: {job.applicationDeadline}</span>}
          {job.contactEmail && <span>📧 {job.contactEmail}</span>}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
          <button onClick={onSelect}
            className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium text-sm flex items-center justify-center gap-1">
            View Full Details <ChevronRight className="w-4 h-4" />
          </button>
          {jobApplicationUrl && (
            <a href={jobApplicationUrl} target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium text-sm flex items-center gap-1"
              title="Search for this position on Indeed">
              <ExternalLink className="w-3.5 h-3.5" /> Search Jobs
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper: format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
};

export default JobSeekerTracker;
